import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Input,
  Label,
  Form,
  FormFeedback,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { apiError } from "../../store/actions";
import { PostData } from "../../services/api";
import Swal from "sweetalert2";
import "./Register.css";
import logodark from "../../assets/images/logo.png";

// Función para traducir mensajes de error comunes al español
const translateError = (errorMessage) => {
  if (!errorMessage) return "Ha ocurrido un error";
  
  const errorLower = errorMessage.toLowerCase();
  
  // Traducciones comunes
  const translations = {
    "email exist": "Este correo electrónico ya está registrado",
    "user not found": "Usuario no encontrado",
    "email unverified": "Email no verificado. Por favor verifica tu correo electrónico",
    "email no verificado": "Email no verificado. Por favor verifica tu correo electrónico",
    "cuenta inactiva": "Cuenta inactiva. Por favor verifica tu correo electrónico para activar tu cuenta",
    "invalid credentials": "Credenciales inválidas",
    "unauthorized": "No autorizado",
    "forbidden": "Acceso denegado",
    "not found": "No encontrado",
    "bad request": "Solicitud inválida",
    "internal server error": "Error interno del servidor",
    "network error": "Error de conexión. Verifica tu internet",
    "timeout": "Tiempo de espera agotado. Intenta nuevamente",
    "verification code incorrect": "Código de verificación incorrecto",
    "código de verificación incorrecto": "Código de verificación incorrecto",
    "verification code expired": "El código de verificación ha expirado",
    "el código de verificación ha expirado": "El código de verificación ha expirado",
    "no hay código de verificación pendiente": "No hay código de verificación pendiente",
  };
  
  // Buscar traducción
  for (const [key, translation] of Object.entries(translations)) {
    if (errorLower.includes(key)) {
      return translation;
    }
  }
  
  // Si no hay traducción, retornar el mensaje original
  return errorMessage;
};

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1: Registro, 2: Verificación
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
      country: "mx",
      acceptTerms: false,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Correo electrónico inválido")
        .required("El correo electrónico es requerido"),
      password: Yup.string()
        .min(8, "La contraseña debe tener al menos 8 caracteres")
        .required("La contraseña es requerida"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], "Las contraseñas no coinciden")
        .required("Debes confirmar tu contraseña"),
      phone: Yup.string()
        .required("El número de teléfono es requerido"),
      acceptTerms: Yup.boolean()
        .oneOf([true], "Debes aceptar los términos y condiciones"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const response = await PostData("/users/signup", {
          email: values.email,
          password: values.password,
          phone: values.phone,
          country: values.country,
          countryCode: "+52",
        });

        if (response?.data) {
          setEmail(values.email);
          setStep(2);
          setResendCooldown(60); // 60 segundos de cooldown
          Swal.fire({
            icon: "success",
            title: "¡Registro exitoso!",
            text: "Se ha enviado un código de verificación a tu correo electrónico",
            confirmButtonColor: "#E9553E",
          });
        }
      } catch (error) {
        const rawErrorMessage = error.response?.data?.error?.message || error.message || "Error al registrar usuario";
        const errorMessage = translateError(rawErrorMessage);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMessage,
          confirmButtonColor: "#E9553E",
        });
      } finally {
        setLoading(false);
      }
    },
  });

  // Manejar código de verificación
  const handleCodeChange = (index, value) => {
    if (value.length > 1) return; // Solo un dígito por input
    
    const newCode = [...verificationCode];
    newCode[index] = value.replace(/\D/g, ""); // Solo números
    setVerificationCode(newCode);

    // Auto-focus siguiente input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split("");
      setVerificationCode(newCode);
      // Focus último input
      setTimeout(() => {
        const lastInput = document.getElementById(`code-5`);
        if (lastInput) lastInput.focus();
      }, 0);
    }
  };

  const handleVerifyCode = async () => {
    const code = verificationCode.join("");
    if (code.length !== 6) {
      Swal.fire({
        icon: "warning",
        title: "Código incompleto",
        text: "Por favor ingresa el código completo de 6 dígitos",
        confirmButtonColor: "#E9553E",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await PostData("/users/verify-email", {
        email: email,
        code: code,
      });

      if (response?.data?.success) {
        Swal.fire({
          icon: "success",
          title: "¡Email verificado!",
          text: "Tu cuenta ha sido activada exitosamente",
          confirmButtonColor: "#E9553E",
        }).then(() => {
          navigate("/login");
        });
      }
    } catch (error) {
      const rawErrorMessage = error.response?.data?.error?.message || error.message || "Código de verificación incorrecto";
      const errorMessage = translateError(rawErrorMessage);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#E9553E",
      });
      // Limpiar código
      setVerificationCode(["", "", "", "", "", ""]);
      document.getElementById("code-0")?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    try {
      const response = await PostData("/users/send-verification-code", {
        email: email,
      });

      if (response?.data?.success) {
        setResendCooldown(60);
        Swal.fire({
          icon: "success",
          title: "Código reenviado",
          text: "Se ha enviado un nuevo código a tu correo electrónico",
          confirmButtonColor: "#E9553E",
        });
      }
    } catch (error) {
      const rawErrorMessage = error.response?.data?.error?.message || error.message || "Error al reenviar código";
      const errorMessage = translateError(rawErrorMessage);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#E9553E",
      });
    } finally {
      setLoading(false);
    }
  };

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-verificar cuando se complete el código
  useEffect(() => {
    if (step === 2 && verificationCode.every(digit => digit !== "") && !loading) {
      const code = verificationCode.join("");
      if (code.length === 6) {
        // Pequeño delay para mejor UX
        const timer = setTimeout(() => {
          handleVerifyCode();
        }, 300);
        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCode, step]);

  useEffect(() => {
    dispatch(apiError(""));
  }, [dispatch]);

  return (
    <React.Fragment>
      <Helmet>
        <title>Registro - CRM IHG</title>
      </Helmet>
      <div className="register-container">
        <div className="register-image-section"></div>
        <div className="register-form-section">
          <div className="register-wrapper">
            <div className="register-header">
              <img
                src={logodark}
                alt="InnovaHubGroup"
                className="register-logo"
              />
              <h1 className="register-title">
                {step === 1 ? "Crear cuenta" : "Verifica tu email"}
              </h1>
              <p className="register-subtitle">
                {step === 1
                  ? "Completa el formulario para comenzar"
                  : `Ingresa el código enviado a ${email}`}
              </p>
            </div>

            <div className="register-body">
              {step === 1 ? (
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}
                >
                  <div className="register-form-group">
                    <label className="register-form-label">Correo electrónico</label>
                    <Input
                      name="email"
                      type="email"
                      className={`register-form-input ${validation.touched.email && validation.errors.email ? 'is-invalid' : ''}`}
                      placeholder="correo@ejemplo.com"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.email || ""}
                    />
                    {validation.touched.email && validation.errors.email && (
                      <div className="register-invalid-feedback">
                        {validation.errors.email}
                      </div>
                    )}
                  </div>

                  <div className="register-form-group">
                    <label className="register-form-label">Contraseña</label>
                    <Input
                      name="password"
                      type="password"
                      className={`register-form-input ${validation.touched.password && validation.errors.password ? 'is-invalid' : ''}`}
                      placeholder="Mínimo 8 caracteres"
                      onChange={(e) => {
                        validation.handleChange(e);
                        // Mostrar campo de confirmación cuando empiece a escribir
                        if (e.target.value.length > 0) {
                          setShowConfirmPassword(true);
                        } else {
                          setShowConfirmPassword(false);
                          // Limpiar confirmPassword si se borra la contraseña
                          validation.setFieldValue('confirmPassword', '');
                        }
                      }}
                      onBlur={validation.handleBlur}
                      value={validation.values.password || ""}
                    />
                    {validation.touched.password && validation.errors.password && (
                      <div className="register-invalid-feedback">
                        {validation.errors.password}
                      </div>
                    )}
                  </div>

                  {showConfirmPassword && (
                    <div className="register-form-group">
                      <label className="register-form-label">Repetir contraseña</label>
                      <Input
                        name="confirmPassword"
                        type="password"
                        className={`register-form-input ${validation.touched.confirmPassword && validation.errors.confirmPassword ? 'is-invalid' : ''}`}
                        placeholder="Repite tu contraseña"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.confirmPassword || ""}
                      />
                      {validation.touched.confirmPassword && validation.errors.confirmPassword && (
                        <div className="register-invalid-feedback">
                          {validation.errors.confirmPassword}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="register-form-group">
                    <label className="register-form-label">Número celular</label>
                    <Input
                      name="phone"
                      type="tel"
                      className={`register-form-input ${validation.touched.phone && validation.errors.phone ? 'is-invalid' : ''}`}
                      placeholder="Ingresa tu número celular"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.phone || ""}
                    />
                    {validation.touched.phone && validation.errors.phone && (
                      <div className="register-invalid-feedback">
                        {validation.errors.phone}
                      </div>
                    )}
                  </div>

                  <div className="register-form-group">
                    <label className="register-form-label">País</label>
                    <Input
                      name="country"
                      type="select"
                      className="register-form-input"
                      onChange={validation.handleChange}
                      value={validation.values.country || "mx"}
                    >
                      <option value="mx">México</option>
                    </Input>
                  </div>

                  <div className="register-checkbox-wrapper">
                    <input
                      type="checkbox"
                      id="acceptTerms"
                      name="acceptTerms"
                      className="register-checkbox"
                      checked={validation.values.acceptTerms || false}
                      onChange={validation.handleChange}
                    />
                    <label htmlFor="acceptTerms" className="register-checkbox-label">
                      Acepto los{" "}
                      <Link to="#" className="text-primary">
                        Términos y Condiciones
                      </Link>
                    </label>
                  </div>
                  {validation.touched.acceptTerms && validation.errors.acceptTerms && (
                    <div className="register-invalid-feedback" style={{ marginTop: "-1rem", marginBottom: "1rem" }}>
                      {validation.errors.acceptTerms}
                    </div>
                  )}

                  <button
                    className="register-submit-btn"
                    type="submit"
                    disabled={loading || !validation.values.acceptTerms}
                  >
                    {loading ? "Registrando..." : "Crear cuenta"}
                  </button>
                </Form>
              ) : (
                <div className="verification-step">
                  <div className="verification-code-inputs">
                    {verificationCode.map((digit, index) => (
                      <input
                        key={index}
                        id={`code-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        className="verification-code-input"
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                        onPaste={index === 0 ? handlePaste : undefined}
                        autoFocus={index === 0}
                        disabled={loading}
                      />
                    ))}
                  </div>

                  <div className="verification-resend">
                    ¿No recibiste el código?{" "}
                    {resendCooldown > 0 ? (
                      <span>Reenviar en {resendCooldown}s</span>
                    ) : (
                      <a onClick={handleResendCode} style={{ cursor: "pointer" }}>
                        Reenviar código
                      </a>
                    )}
                  </div>

                  <button
                    className="register-submit-btn"
                    onClick={handleVerifyCode}
                    disabled={loading || verificationCode.some(d => d === "")}
                    style={{ marginTop: "1.5rem" }}
                  >
                    {loading ? "Verificando..." : "Verificar código"}
                  </button>
                </div>
              )}
            </div>

            <div className="register-footer">
              <p>
                {step === 1 ? (
                  <>
                    ¿Ya tienes una cuenta?{" "}
                    <Link to="/login" className="register-footer-link">
                      Inicia sesión
                    </Link>
                  </>
                ) : (
                  <a
                    onClick={() => setStep(1)}
                    className="register-footer-link"
                    style={{ cursor: "pointer" }}
                  >
                    ← Volver al registro
                  </a>
                )}
              </p>
              <p className="register-footer-copyright">
                © {new Date().getFullYear()} InnovaHubGroup{" "}
                <i className="mdi mdi-heart"></i>
              </p>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Register;
